import { Button, Heading, Inline, Modal, styled } from 'canoe';
import { ENDPOINTS, useActions } from 'hammock';
import React, { useEffect, useState } from 'react';

const StyledLink = styled.span`
  cursor: pointer;
  text-decoration: underline;
`;

export type UsedContent = {
  id: string;
  contentId: string;
  segmentId: string;
};

export interface DeleteCollectionProps {
  context?: {
    canDelete?: boolean;
  };
  collectionId: string;
  collectionName?: string;
  onCollectionDeleteSuccess?: VoidFunction;
  onCollectionDeleteFail?: VoidFunction;
  onRedirectToEditor: (to: string) => void;
}

export const DeleteCollection: React.FC<DeleteCollectionProps> = ({
  context,
  collectionName,
  collectionId,
  onCollectionDeleteSuccess,
  onCollectionDeleteFail,
  onRedirectToEditor,
}: DeleteCollectionProps) => {
  const [usedContents, setUsedContents] = useState<UsedContent[]>();
  const { delete: deleteCollection } = useActions(ENDPOINTS.collections);

  const [collectionToDelete, setCollectionToDelete] = useState<boolean>(false);
  const closeModal = () => setCollectionToDelete(false);

  const { getAll } = useActions(ENDPOINTS.contentQuery);
  useEffect(() => {
    const getContent = async () => {
      try {
        const contents = await getAll({
          cache: false,
          query: {
            finder: 'byCollectionId',
            id: collectionId,
          },
        });
        if (contents) {
          setUsedContents((contents as UsedContent[]) || []);
        } else {
          throw new Error('failed to fetch associated content');
        }
      } catch (e) {
        console.warn('error on fetching contentQuery', e);
      }
    };
    getContent();
  }, [getAll, collectionId]);

  const onDeleteCollection = async () => {
    try {
      await deleteCollection({
        id: collectionId,
      });
      onCollectionDeleteSuccess?.();
    } catch (error) {
      onCollectionDeleteFail?.();
    }
  };

  return (
    <>
      {collectionToDelete && usedContents ? (
        <Modal closeOnClickOutside onClose={closeModal} severity="Danger">
          <Modal.Header title={`Delete ${collectionName}?`} />
          <Modal.Body>
            {usedContents?.length ? (
              <>
                You cannot delete this collection as it is being used in the following content(s):
                <br />
                <br />
                <ul data-cy="recordIds-list">
                  {usedContents.map((content) => (
                    <li key={content.id}>
                      <StyledLink
                        onClick={() =>
                          onRedirectToEditor(
                            `/editor/${content.contentId}/content/${content.segmentId}`,
                          )
                        }
                      >
                        {content.id.split('~')[0]} (click to open in the editor)
                      </StyledLink>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                This will delete the collection and all its data permanently. This action is not
                reversible.
              </>
            )}
          </Modal.Body>
          <Modal.Footer
            secondaryAction={{
              onClick: closeModal,
              variant: 'neutral',
              children: 'Cancel',
            }}
            primaryAction={{
              variant: 'danger',
              onClick: onDeleteCollection,
              children: 'Yes, delete forever',
              disabled: !!usedContents.length,
            }}
          />
        </Modal>
      ) : null}
      <Heading element="h2" align="left" size="XS">
        Delete Collection
      </Heading>
      You can only delete a Collection if it’s not being used in any content.
      <Inline justify="flex-end">
        <Button
          data-cy="delete"
          disabled={!context?.canDelete}
          variant="danger"
          onClick={() => setCollectionToDelete(true)}
        >
          Delete
        </Button>
      </Inline>
    </>
  );
};
